import { DrawerRow, PickRow } from '@bo-components/playerPacks';
import { CloseIconButton } from '@library/components/buttons';
import { CountdownTimer } from '@bo-utils/countdownTimer';
import { Drawer } from '@library/components/navigation/Drawer';
import type { LeaguesList } from '@library/types/leagues';
import type { PlayerPack } from '@library/types/playerPacks/playerPacks';
import { useMemo } from 'react';
import { usePlayerPacksExcludedStates } from '@bo-hooks/playerPacks/usePlayerPacksExcludedStates';

export interface PlayerPackDrawerProps {
  isOpen: boolean;
  leaguesList: LeaguesList;
  onCloseDrawer: () => void;
  playerPack: PlayerPack;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
}

export const PlayerPackDrawer = ({
  isOpen,
  leaguesList,
  onCloseDrawer,
  playerPack,
  size = 'medium'
}: PlayerPackDrawerProps) => {
  const { allowedStates } = usePlayerPacksExcludedStates();

  const mapIdToState = useMemo(() => {
    return (stateId: string) => {
      return (
        allowedStates.find((state) => state.id === stateId)?.name || 'Unknown'
      );
    };
  }, [allowedStates]);

  const {
    title,
    imageUrl,
    placeByTime,
    canFade,
    excludedStates = [],
    picks = []
  } = playerPack;

  const soonestGameTime = useMemo(
    () =>
      picks.length
        ? picks.reduce((min, pick) =>
            new Date(pick.gameTime) < new Date(min.gameTime) ? pick : min
          ).gameTime
        : '',
    [picks]
  );

  return (
    <Drawer
      isOpen={isOpen}
      onCloseDrawer={onCloseDrawer}
      direction="right"
      size={size}
    >
      <div className="border-soFresh-140 text-soClean-100 flex h-16 items-center border-b px-6 py-2">
        <div className="flex h-full flex-col justify-evenly text-lg font-semibold">
          {title}
          <div className="text-soClean-140 text-sm font-thin">
            {'First game starts in '}
            <CountdownTimer
              targetDate={soonestGameTime}
              className="text-status-caution"
            />
          </div>
        </div>
        <CloseIconButton handleOnClick={onCloseDrawer} className="ml-auto" />
      </div>
      <div className="flex flex-col gap-5 overflow-auto p-6">
        <DrawerRow label="Title" value={title} />
        <DrawerRow label="Image URL" value={imageUrl} />
        <div>
          <div className="text-soClean-170 text-sm font-light">Preview</div>
          <div className="border-soFresh-140 mt-1 flex items-center justify-center rounded-md border">
            <img
              src={imageUrl || ''}
              alt="Preview"
              className="size-full rounded-md object-cover"
            />
          </div>
        </div>
        <div>
          <div className="text-soClean-170 text-sm font-light">Lineup</div>
          <div className="border-soFresh-140 mt-1 flex flex-col items-center justify-center rounded-md border">
            {picks.length > 0 &&
              picks.map((pick) => (
                <PickRow
                  key={pick.projectionId}
                  pick={pick}
                  leaguesList={leaguesList}
                />
              ))}
          </div>
        </div>
        <DrawerRow label="Place by" value={placeByTime} />
        <DrawerRow label="Can fade" value={String(canFade)} />
        <DrawerRow
          label="Excluded states"
          value={
            excludedStates.map((id) => mapIdToState(id)).join(', ') || 'None'
          }
        />
      </div>
    </Drawer>
  );
};
